/**
 * MapLabels Custom Hooks
 * Shared hooks for map label components
 */

import { useState, useCallback, useMemo, useRef, useLayoutEffect } from 'react';

/**
 * Custom hook for hover state
 */
export const useHover = () => {
  const [isHovered, setIsHovered] = useState(false);
  const handlers = useMemo(() => ({
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
  }), []);
  return [isHovered, handlers];
};

/**
 * Custom hook to fit label width to wrapped text
 * Measures actual line widths and sets container to max line width
 * Re-measures after fonts load and after a delay to catch late font loading
 */
export const useWrappedTextWidth = (textContent) => {
  const labelRef = useRef(null);
  const textRef = useRef(null);
  const [fittedWidth, setFittedWidth] = useState(null);

  // Measurement function
  const measure = useCallback(() => {
    const textEl = textRef.current;
    const labelEl = labelRef.current;
    if (!textEl || !labelEl) return;

    // Get computed line height to determine if text has actually wrapped
    const textStyles = window.getComputedStyle(textEl);
    const lineHeight = parseFloat(textStyles.lineHeight) || parseFloat(textStyles.fontSize) * 1.3;
    const textHeight = textEl.getBoundingClientRect().height;

    // If height is less than ~1.5 lines, text hasn't wrapped
    if (textHeight < lineHeight * 1.5) {
      setFittedWidth(null);
      return;
    }

    // Text has wrapped - use Range API to get per-line text rects
    // (getClientRects on span in flex container returns single rect)
    const range = document.createRange();
    range.selectNodeContents(textEl);
    const rects = range.getClientRects();
    let maxTextLineWidth = 0;
    for (let i = 0; i < rects.length; i += 1) {
      maxTextLineWidth = Math.max(maxTextLineWidth, rects[i].width);
    }

    // Get label's computed styles
    const labelStyles = window.getComputedStyle(labelEl);
    const paddingLeft = parseFloat(labelStyles.paddingLeft) || 0;
    const paddingRight = parseFloat(labelStyles.paddingRight) || 0;

    // For non-flex labels, just use text width + padding
    const isFlexContainer = labelStyles.display === 'flex';

    if (!isFlexContainer) {
      // Block-stacked label: width must accommodate the widest child line as
      // well as any sibling rows (e.g. a subtitle below the title). Block
      // children stretch to the parent's content width, so measuring their
      // bounding rect is meaningless — use the Range API to get the actual
      // text content width of each sibling.
      let maxChildWidth = maxTextLineWidth;
      const children = labelEl.children;
      for (let i = 0; i < children.length; i += 1) {
        const child = children[i];
        if (child === textEl) continue;
        const childRange = document.createRange();
        childRange.selectNodeContents(child);
        const childRects = childRange.getClientRects();
        let childTextWidth = 0;
        for (let j = 0; j < childRects.length; j += 1) {
          if (childRects[j].width > childTextWidth) {
            childTextWidth = childRects[j].width;
          }
        }
        if (childTextWidth > maxChildWidth) maxChildWidth = childTextWidth;
      }
      setFittedWidth(Math.ceil(maxChildWidth + paddingLeft + paddingRight));
      return;
    }

    // For flex containers, we need to account for sibling elements
    // Calculate total width of all children based on their natural sizes
    let totalChildrenWidth = 0;
    const children = labelEl.children;

    for (let i = 0; i < children.length; i += 1) {
      const child = children[i];
      const childStyles = window.getComputedStyle(child);
      const marginLeft = parseFloat(childStyles.marginLeft) || 0;
      const marginRight = parseFloat(childStyles.marginRight) || 0;

      if (child === textEl) {
        // For the text element, use the max line width (not its current width)
        totalChildrenWidth += maxTextLineWidth + marginLeft + marginRight;
      } else {
        // For other children, use their rendered width
        totalChildrenWidth += child.getBoundingClientRect().width + marginLeft + marginRight;
      }
    }

    const idealWidth = Math.ceil(totalChildrenWidth + paddingLeft + paddingRight);
    setFittedWidth(idealWidth);
  }, []);

  // Initial measurement
  useLayoutEffect(() => {
    measure();
  }, [textContent, measure]);

  // Schedule remeasurements after paint to catch font loading
  useLayoutEffect(() => {
    let mounted = true;

    const triggerRemeasure = () => {
      if (mounted) {
        // Use requestAnimationFrame to ensure we measure after paint
        requestAnimationFrame(() => {
          if (mounted) {
            measure();
          }
        });
      }
    };

    // Remeasure after fonts are ready
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(triggerRemeasure);
    }

    // Also remeasure after a short delay as a fallback
    const timer = setTimeout(triggerRemeasure, 150);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [measure]);

  return { labelRef, textRef, fittedWidth };
};
